import { useState } from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
// Removed unused Select imports
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { 
  DateRange, 
  DateRangePreset, 
  getDateRangeForPreset,
  isValidDateRange 
} from '@/lib/schemas/dateRange.schemas';
// Inline cn function to avoid import issues
const cn = (...classes: (string | undefined | null | false)[]) => {
  return classes.filter(Boolean).join(' ');
};

/**
 * DateRangeSelector Component
 * 
 * Date range picker for filtering time-based data and metrics.
 * 
 * Responsibilities:
 * - Display date range picker
 * - Validate range (max 31 days)
 * - Handle range selection
 * - Show formatted date range text
 */

interface DateRangeSelectorProps {
  selectedRange: DateRange | null;
  onRangeChange: (range: DateRange) => void;
  maxDays?: number;
  maxDate?: Date;
}

const DATE_PRESETS: { value: DateRangePreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_14_days', label: 'Last 14 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
];

export function DateRangeSelector({
  selectedRange,
  onRangeChange,
  maxDays = 31,
}: DateRangeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempRange, setTempRange] = useState<{ from?: Date; to?: Date }>({});

  const formatDateRange = (range: DateRange): string => {
    const normalizeDate = (date: Date | string): Date => {
      return date instanceof Date ? date : new Date(date);
    };

    const normalizedFrom = normalizeDate(range.from);
    const normalizedTo = normalizeDate(range.to);

    // Same day
    if (normalizedFrom.toDateString() === normalizedTo.toDateString()) {
      return format(normalizedFrom, 'MMM d');
    }

    // Same year
    if (normalizedFrom.getFullYear() === normalizedTo.getFullYear()) {
      return `${format(normalizedFrom, 'MMM d')} - ${format(normalizedTo, 'MMM d')}`;
    }

    // Different years
    return `${format(normalizedFrom, 'MMM d, yy')} - ${format(normalizedTo, 'MMM d, yy')}`;
  };

  const handlePresetChange = (preset: DateRangePreset) => {
    const range = getDateRangeForPreset(preset);
    if (!range || !isValidDateRange(range, maxDays)) return;

    onRangeChange(range);
    setIsOpen(false);
  };

  const handleCalendarSelect = (date: Date | { from?: Date; to?: Date } | undefined) => {
    if (!date) {
      setTempRange({});
      return;
    }

    // Handle single date selection
    if (date instanceof Date) {
      const range = { from: date, to: date };
      setTempRange(range);
      const dateRange: DateRange = { from: date, to: date };
      if (isValidDateRange(dateRange, maxDays)) {
        onRangeChange(dateRange);
        setIsOpen(false);
        setTempRange({});
      }
      return;
    }

    // Handle range selection
    const range = date;
    setTempRange(range);

    if (range.from && range.to) {
      const dateRange: DateRange = { from: range.from, to: range.to };
      if (isValidDateRange(dateRange, maxDays)) {
        onRangeChange(dateRange);
        setIsOpen(false);
        setTempRange({});
      }
    }
  };

  const getCurrentPreset = (): DateRangePreset | null => {
    if (!selectedRange) return null;

    const normalizeDate = (date: Date | string): Date => {
      return date instanceof Date ? date : new Date(date);
    };

    const normalizedRange = {
      from: normalizeDate(selectedRange.from),
      to: normalizeDate(selectedRange.to),
    };

    for (const preset of DATE_PRESETS) {
      const presetRange = getDateRangeForPreset(preset.value);
      if (presetRange) {
        const isSameDay = (date1: Date, date2: Date) => {
          return date1.toDateString() === date2.toDateString();
        };

        if (
          isSameDay(normalizedRange.from, presetRange.from) &&
          isSameDay(normalizedRange.to, presetRange.to)
        ) {
          return preset.value;
        }
      }
    }

    return null;
  };

  const currentPreset = getCurrentPreset();
  const currentPresetLabel = currentPreset 
    ? DATE_PRESETS.find(p => p.value === currentPreset)?.label
    : 'Custom range';

  return (
    <div className="flex items-center gap-2">
      {/* Compact Date Range Picker */}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "h-9 px-3 justify-start text-left font-normal min-w-[200px] bg-background hover:bg-accent/50 border-border/50",
              !selectedRange && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 opacity-50" />
            {selectedRange ? (
              <div className="flex items-center justify-between w-full">
                <span className="text-sm font-medium">
                  {formatDateRange(selectedRange)}
                </span>
                {currentPreset && (
                  <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded ml-2">
                    {currentPresetLabel}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-sm">Select range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 shadow-lg border-border/50" align="start">
          <div className="flex">
            {/* Preset Options */}
            <div className="w-32 p-2 border-r border-border/50 bg-muted/30">
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Quick Select
                </div>
                {DATE_PRESETS.map((preset) => {
                  const range = getDateRangeForPreset(preset.value);
                  const isValid = range ? isValidDateRange(range, maxDays) : false;
                  const isSelected = currentPreset === preset.value;
                  
                  return (
                    <button
                      key={preset.value}
                      onClick={() => isValid && handlePresetChange(preset.value)}
                      disabled={!isValid}
                      className={cn(
                        "w-full text-left px-2 py-1 rounded text-sm transition-colors",
                        isSelected 
                          ? "bg-primary text-primary-foreground" 
                          : "hover:bg-accent hover:text-accent-foreground",
                        !isValid && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      <div className="font-medium text-xs">{preset.label}</div>
                      {range && isValid && (
                        <div className="text-xs opacity-70 mt-0.5">
                          {formatDateRange(range)}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Calendar */}
            <div className="p-2">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={selectedRange ? new Date(selectedRange.from) : new Date()}
                selected={{
                  from: tempRange.from || (selectedRange ? new Date(selectedRange.from) : undefined),
                  to: tempRange.to || (selectedRange ? new Date(selectedRange.to) : undefined),
                }}
                onSelect={handleCalendarSelect}
                numberOfMonths={1}
                disabled={(date) => date > new Date()}
                className="rounded-md"
                classNames={{
                  day_range_start: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-l-md",
                  day_range_end: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-r-md",
                  day_range_middle: "bg-primary/20 text-primary-foreground hover:bg-primary/30 aria-selected:bg-primary/20 aria-selected:text-foreground",
                  day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                }}
              />
              {/* Visual feedback for temp selection */}
              {tempRange.from && !tempRange.to && (
                <div className="text-xs text-muted-foreground mt-2 px-2 text-center">
                  Select end date to complete range
                </div>
              )}
              {tempRange.from && tempRange.to && (
                <div className="text-xs text-primary mt-2 px-2 font-medium text-center">
                  {formatDateRange({ from: tempRange.from, to: tempRange.to })}
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Reset Button */}
      {selectedRange && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRangeChange(getDateRangeForPreset('last_7_days')!)}
          className="h-9 px-2 text-muted-foreground hover:text-foreground text-xs"
        >
          Reset
        </Button>
      )}
    </div>
  );
}